import React, { useState } from 'react';

import { DateTime } from 'luxon';

import { Alert, Breadcrumb, Panel } from '@shared/components/bootstrap';

import { AvailabilityControls } from '@admin/components/availability/controls';
import { PageHeader } from '@admin/components/helpers/page_header';
import { Pagination } from '@admin/components/pagination';
import { Spinner } from '@admin/components/spinner';
import { availabilityRescheduleOffersNewURL } from '@admin/config/routes';
import { usePermission } from '@admin/hooks/use_permission';
import { useTitle } from '@admin/hooks/use_title';
import { AVAILABILITY_ADMIN } from '@admin/libraries/access_control_catalog.generated';
import { client } from '@admin/libraries/apollo';
import {
  Availability__RescheduleOfferFiltersInput,
  AvailabilityRescheduleOffersDocument,
  useAvailabilityRescheduleOffersDeleteMutation,
  useAvailabilityRescheduleOffersQuery,
} from '@admin/schema';

import { Entries } from './entries';
import { Filters } from './filters';

const DEFAULT_FILTERS: Availability__RescheduleOfferFiltersInput = {
  fromDate: DateTime.local().toISODate(),
  tillDate: DateTime.local().plus({ days: 30 }).toISODate(),
  regionIDs: [],
  orderClassifications: [],
};

export const List: React.FC = () => {
  useTitle('Availability Reschedule Offers');
  const [filters, setFilters] = useState<Availability__RescheduleOfferFiltersInput>(DEFAULT_FILTERS);
  const [page, setPage] = useState<number>();
  const [selectedIDs, setSelectedIDs] = useState<string[]>([]);

  const { data, loading: querying } = useAvailabilityRescheduleOffersQuery({
    client,
    variables: {
      page,
      filters,
    },
  });

  const [destroy, { data: destroyData, loading: destroying }] = useAvailabilityRescheduleOffersDeleteMutation({
    client,
    awaitRefetchQueries: true,
    refetchQueries: [
      {
        query: AvailabilityRescheduleOffersDocument,
        variables: {
          page,
          filters,
        },
      },
    ],
    onCompleted: () => setSelectedIDs([]),
  });

  const onDestroy = async () => {
    await destroy({
      variables: {
        IDs: selectedIDs,
      },
    });
  };

  const onPageChange = (pageNumber: number) => {
    setSelectedIDs([]);
    setPage(pageNumber);
  };

  const canModify = usePermission(AVAILABILITY_ADMIN.resource, AVAILABILITY_ADMIN.actions.update);
  const paginated = data?.paginated;
  const options =
    paginated?.results.reduce((acc, { id, expired }) => {
      if (!expired) {
        acc.push(id);
      }
      return acc;
    }, [] as string[]) ?? [];
  const loading = querying || destroying;

  return (
    <>
      <PageHeader>
        <Breadcrumb>
          <Breadcrumb.Item active>Availability Reschedule Offers</Breadcrumb.Item>
        </Breadcrumb>
      </PageHeader>
      <Panel>
        <Panel.Body>
          {destroyData?.result?.error && <Alert style="danger">{destroyData.result.error}</Alert>}
          <Filters filters={filters} onChange={setFilters} />
          <AvailabilityControls
            createHref={availabilityRescheduleOffersNewURL()}
            loading={loading}
            options={options}
            selectedOptions={selectedIDs}
            create={canModify}
            destroy={canModify}
            onDestroy={onDestroy}
            setSelectedOptions={setSelectedIDs}
          />
          {loading && <Spinner />}
          {paginated && (
            <Entries
              availabilityRescheduleOffers={paginated.results}
              selectedOptions={selectedIDs}
              setSelectedOptions={setSelectedIDs}
            />
          )}
          {paginated && <Pagination onPage={onPageChange} pagination={paginated.pagination} />}
        </Panel.Body>
      </Panel>
    </>
  );
};
